import React from 'react';
import { Sparkles } from 'lucide-react';
import type { HeadlineResult } from '../../utils/intelligence';
import { intelligenceCopy } from '../../copy/intelligence';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// HeadlineInsight — the one-line signal that leads every Intelligence-cluster
// report. Keeps the user focused on what the numbers mean before they see the
// numbers themselves. When the derivation returns `null` (not enough data yet)
// we render a neutral note so the layout doesn't collapse into the chart.
// ---------------------------------------------------------------------------

interface HeadlineInsightProps {
  result: HeadlineResult | null;
  variant?: 'primary' | 'compact';
}

export function HeadlineInsight({ result, variant = 'primary' }: HeadlineInsightProps) {
  const isCompact = variant === 'compact';
  return (
    <div
      className={cn(
        'mb-4 rounded-2xl border border-primary bg-primary/[0.04]',
        isCompact ? 'p-3' : 'p-4',
      )}
    >
      <div className="flex items-start gap-2">
        <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
        <div className="min-w-0 flex-1">
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.12em] leading-none text-primary">
            {intelligenceCopy.headlineLabel}
          </p>
          {result ? (
            <>
              <p className="text-sm font-medium">{result.headline}</p>
              {result.delta && (
                <p className="mt-1 text-sm text-muted-foreground">{result.delta}</p>
              )}
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">{intelligenceCopy.noSignalYet}</p>
              <p className="mt-1 text-xs text-muted-foreground">{intelligenceCopy.noSignalHint}</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default HeadlineInsight;
